import { MoveRight, ArrowUpRight, Instagram, Sparkles, Compass, UserCheck, CalendarRange, Megaphone, Ticket, PenTool, ClipboardCheck, HeartHandshake } from "lucide-react";
import { Reveal, ChapterHead } from "./Reveal";
import PhotoSlot from "./PhotoSlot";

// PHOTOS CLIENT — duo chapitre 01
const IMG_SPA = "/images/pratique-rideau.png";
const IMG_YOGA = "/images/fleurs-rooftop.png";

const PAINS = [
  ["Pas le temps de piloter un projet wellness", "On gère tout, de la stratégie au suivi terrain"],
  ["Pas l'expertise pour juger la qualité des intervenant·e·s", "Un réseau déjà qualifié et vérifié"],
  ["Pas de réseau wellness constitué", "On l'apporte, prêt à activer"],
  ["Pas de méthode pour en faire une offre rentable", "Une stratégie pensée pour générer du revenu"],
];

const SERVICES = [
  ["Stratégie wellness rémunératrice", "On pense un modèle qui génère du revenu, pas juste de l'occupation.", "lg:col-span-2", Sparkles],
  ["Positionnement", "L'identité wellness qui correspond à votre lieu et votre clientèle.", "", Compass],
  ["Sélection des intervenant·e·s", "Les bons profils, déjà qualifiés et vérifiés.", "", UserCheck],
  ["Programmation", "Un calendrier cohérent, pas des dates isolées.", "lg:col-span-2", CalendarRange],
  ["Communication & marketing", "La visibilité de l'offre, du contenu à la diffusion.", "", Megaphone],
  ["Ticketing", "Réservations et paiements, de bout en bout.", "", Ticket],
  ["Visuels & site", "Les supports nécessaires, jusqu'au site si besoin.", "", PenTool],
  ["Follow-up opérationnel", "Chaque événement suivi du début à la fin, sans que vous interveniez.", "", ClipboardCheck],
  ["Relation avec les pros", "La coordination avec chaque intervenant·e engagé·e.", "lg:col-span-2", HeartHandshake],
];

const UPSELL = ["Performance", "Ambition", "Confiance en soi", "Leadership", "Gestion du stress"];

export default function Etablissements() {
  return (
    <section id="etablissements" data-testid="section-etablissements" className="relative py-28 md:py-36 overflow-hidden">
      <div className="orb w-[460px] h-[460px] bg-[#FF8E53] blur-[130px] opacity-30 top-40 -left-40" aria-hidden="true" />

      <div className="wrap relative">
        <ChapterHead
          num="01"
          badge="Pour les établissements · 100% clé en main"
          title={<>Le wellness, oui. Le temps de s'en occuper, <span className="font-serifit italic font-normal text-[#E8431F]">non.</span></>}
          note="C'est le temps, l'expertise et le réseau. On apporte les trois. On ne place pas un cours de yoga dans un créneau libre : on pense, construit et pilote une stratégie wellness qui rapporte."
        />

        {/* Pain -> Réponse */}
        <div className="max-w-[980px]">
          {PAINS.map(([pain, res], i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div
                data-testid={`pain-row-${i + 1}`}
                className="group grid md:grid-cols-[1fr_56px_1fr] items-center gap-3 md:gap-6 py-6 border-t border-[#1C0E14]/10 last:border-b"
              >
                <p className="text-[#5F5258] font-medium">{pain}</p>
                <span className="hidden md:flex justify-center text-[#FF4B3E] transition-transform duration-300 group-hover:translate-x-2">
                  <MoveRight size={20} />
                </span>
                <p className="font-display font-semibold text-lg md:text-xl">{res}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Duo photo éditorial */}
        <div className="grid md:grid-cols-[1.35fr_1fr] gap-5 my-20 md:my-28">
          <PhotoSlot src={IMG_SPA} index="02" caption="Pratique en studio" ratio="aspect-[16/10]" speed={60} />
          <PhotoSlot src={IMG_YOGA} index="03" caption="Détails qui comptent" ratio="aspect-[16/10] md:aspect-auto" speed={90} />
        </div>

        {/* Bento services */}
        <Reveal>
          <p className="eyebrow text-[#FF4B3E] mb-10">Tout ce qu'on prend en charge, à votre place</p>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map(([title, desc, span, Icon], i) => (
            <Reveal key={title} delay={(i % 3) * 0.07} className={span}>
              <div
                data-testid={`service-card-${i + 1}`}
                className="group glass rounded-3xl p-7 md:p-8 h-full min-h-[150px] flex flex-col justify-between gap-6 transition-transform duration-500 hover:-translate-y-1.5"
              >
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#E8431F]/10 text-[#E8431F] transition-all duration-500 group-hover:rotate-12 group-hover:bg-[#E8431F] group-hover:text-[#FFF1E4]">
                  <Icon size={19} strokeWidth={1.8} />
                </span>
                <div>
                  <h3 className="font-display font-semibold text-xl md:text-2xl leading-tight mb-2">{title}</h3>
                  <p className="text-[0.9rem] text-[#5F5258] font-medium leading-relaxed">{desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Bandeau clé en main */}
        <Reveal className="mt-4">
          <div
            data-testid="zero-effort-banner"
            className="relative overflow-hidden rounded-[32px] bg-[#7F762D] text-[#FF86A5] px-8 py-12 md:px-14 md:py-16 flex flex-wrap items-center justify-between gap-8"
          >
            <div className="orb w-[300px] h-[300px] bg-[#FF86A5] blur-[110px] opacity-25 -top-20 -right-10" aria-hidden="true" />
            <p className="font-serifit italic text-2xl md:text-4xl leading-tight max-w-[26ch] relative">
              Votre seul travail : mettre à disposition vos temps creux
            </p>
          </div>
        </Reveal>

        {/* Formats */}
        <div className="grid md:grid-cols-2 gap-4 mt-24 md:mt-32">
          <Reveal>
            <div data-testid="format-card-whitelabel" className="glass rounded-[32px] p-9 md:p-12 h-full">
              <p className="eyebrow text-[#5F5258] mb-6">Format 01</p>
              <h3 className="font-display font-semibold text-3xl md:text-4xl mb-4">Marque blanche</h3>
              <p className="text-[#5F5258] font-medium leading-relaxed max-w-[38ch]">
                Le·la professionnel·le intervient sous votre identité, sans mention de
                Douceur Power. Votre marque, notre machinerie.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="format-card-coproduction" className="relative overflow-hidden rounded-[32px] p-9 md:p-12 h-full bg-gradient-to-br from-[#FF4B3E] to-[#FF2A7A] text-[#F6F1EC]">
              <div className="orb w-[260px] h-[260px] bg-[#FFB347] blur-[80px] opacity-50 -bottom-16 -right-10" aria-hidden="true" />
              <p className="eyebrow text-white/70 mb-6 relative">Format 02</p>
              <h3 className="font-display font-semibold text-3xl md:text-4xl mb-4 relative">
                Coproduction <span className="font-serifit italic font-normal">Douceur Power</span>
              </h3>
              <p className="text-white/85 font-medium leading-relaxed max-w-[38ch] relative">
                Coportée par notre marque : une communauté qualifiée de 86K followers sur
                Instagram, pour une visibilité immédiate.
              </p>
              <p className="relative mt-8 font-display font-semibold text-6xl">86K</p>
              <a
                href="https://www.instagram.com/douceur_power/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="copro-instagram"
                className="relative mt-3 inline-flex items-center gap-2 font-mono2 text-sm text-white/85 underline underline-offset-4 hover:text-white transition-colors self-start"
              >
                <Instagram size={15} />
                @douceur_power
              </a>
            </div>
          </Reveal>
        </div>

        {/* Upsell B2B */}
        <div className="mt-24 md:mt-32">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
              <h3 className="font-display font-semibold text-3xl md:text-5xl max-w-[16ch]">
                Un upsell tout prêt pour vos clients <span className="font-serifit italic font-normal text-[#FF4B3E]">B2B</span>
              </h3>
              <p className="max-w-[34ch] text-[#5F5258] font-medium">
                Vous accueillez déjà des entreprises. Proposez-leur des activations wellness pensées pour elles.
              </p>
            </div>
          </Reveal>
          <div className="flex flex-wrap gap-3">
            {UPSELL.map((t, i) => (
              <Reveal key={t} delay={i * 0.05}>
                <span
                  data-testid={`upsell-tag-${i + 1}`}
                  className="tag-pill glass inline-flex items-center gap-2 cursor-default hover:bg-[#E8431F] hover:text-[#FFF6EE] hover:border-transparent"
                >
                  {t}
                  <ArrowUpRight size={14} className="text-[#FF4B3E]" />
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
